import { get } from '@/lib/api';

/**
 * Consultas do painel inicial.
 *
 * O tipo do chamado é declarado aqui em versão reduzida, e não importado de
 * `@/features/tickets`, porque a casca não deve depender do contrato interno
 * de uma tela. O painel lê seis campos; se a lista de chamados evoluir o
 * dela, nada aqui precisa acompanhar.
 */

export interface ChamadoDoPainel {
  id: string;
  number: string;
  subject: string;
  statusCategory: 'NEW' | 'IN_PROGRESS' | 'PENDING' | 'RESOLVED' | 'CLOSED' | 'CANCELLED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  openedAt: string;
  dueAt: string | null;
}

interface PaginaDeChamados {
  items: ChamadoDoPainel[];
  nextCursor: string | null;
}

/**
 * Teto imposto pela API (`ListTicketsQuery.limit` tem `@Max(200)`).
 *
 * Importa porque `GET /tickets` devolve página, não total: a contagem do
 * painel é o tamanho da página, e além deste teto ela vira um piso — daí o
 * `parcial` em `Contagem`.
 */
export const LIMITE_DA_API = 200;

export interface Contagem {
  valor: number;
  /** `true` quando havia mais páginas: o número é um mínimo, não um total. */
  parcial: boolean;
}

type Filtro = Record<string, string | boolean | undefined>;

async function listar(filtro: Filtro): Promise<PaginaDeChamados> {
  const params = new URLSearchParams({ limit: String(LIMITE_DA_API) });
  for (const [chave, valor] of Object.entries(filtro)) {
    if (valor === undefined || valor === false) continue;
    params.set(chave, String(valor));
  }
  return get<PaginaDeChamados>(`/tickets?${params.toString()}`);
}

function contar(pagina: PaginaDeChamados): Contagem {
  return { valor: pagina.items.length, parcial: pagina.nextCursor !== null };
}

/**
 * Chamados abertos, só as raízes.
 *
 * `rootsOnly` porque subchamado aparece dentro do pai: contá-lo de novo aqui
 * inflaria o indicador com trabalho que já está representado.
 */
export async function chamadosAbertos(): Promise<{
  contagem: Contagem;
  recentes: ChamadoDoPainel[];
  vencendo: Contagem;
}> {
  const pagina = await listar({ openOnly: true, rootsOnly: true });
  return {
    contagem: contar(pagina),
    recentes: pagina.items.slice(0, 6),
    vencendo: vencendoEm24h(pagina),
  };
}

export async function chamadosAtribuidosA(assigneeId: string): Promise<Contagem> {
  return contar(await listar({ openOnly: true, assigneeId }));
}

/**
 * Aguardando terceiros = categoria `PENDING`.
 *
 * Sem `openOnly` junto: o serviço sobrescreve `statusCategory` quando os dois
 * vêm, e o filtro de status sumiria em silêncio. `PENDING` já é aberto por
 * definição.
 */
export async function chamadosPendentes(): Promise<Contagem> {
  return contar(await listar({ status: 'PENDING' }));
}

const VINTE_E_QUATRO_HORAS = 24 * 60 * 60 * 1000;

/**
 * Quantos abertos vencem nas próximas 24 h.
 *
 * Derivado de `dueAt` sobre a mesma página de abertos, e não de um endpoint:
 * a API expõe SLA por chamado (`GET /tickets/:id/sla`), nunca agregado. Se a
 * página veio truncada o número é um piso, e a interface diz isso — melhor um
 * "18+" honesto do que um 18 que parece total.
 */
function vencendoEm24h(pagina: PaginaDeChamados, agora = Date.now()): Contagem {
  const limite = agora + VINTE_E_QUATRO_HORAS;
  const valor = pagina.items.filter((chamado) => {
    if (!chamado.dueAt) return false;
    return new Date(chamado.dueAt).getTime() <= limite;
  }).length;
  return { valor, parcial: pagina.nextCursor !== null };
}
